import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity } from 'react-native';

import {
  get as _get,
  filter as _filter,
  findIndex as _findIndex,
  isFunction as _isFunction,
} from 'lodash';
import PropTypes from 'prop-types';

import { VFText, VFButton, Icon } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { getThemeImages as getSohoThemeImages } from '../../../Themes/Modes';
import SohoFrameworkTestKeys from '../../../Utils/SohoFrameworkTestKeys';
import SelectionItem from '../SelectionItem';

import styles from './SelectionList.Styles';

function SelectionList({
  title,
  emptyText,
  dataConfig: {
    data,
    dataTitleKey,
    dataSubTitleKey,
    dataSubTitlePlaceholdersKey,
    defaultNumberOfShownData,
  },
  testKeys: {
    selectionTitleTestKey,
    titleTestKey,
    selectTestKey,
    primaryButtonTestKey,
    secondaryButtonTestKey,
  } = {},
  images,
  withSelect,
  withShowMore,
  onPrimaryButtonPress,
  onSecondaryButtonPress,
  onSelectPress,
  renderButtonsSection,
  selectAllButtonTitle,
  showMoreButtonTitle,
  showLessButtonTitle,
  primaryButtonTitle,
  secondaryButtonTitle,
  defaultSelectedDataState,
  ...props
}) {
  const [selectedData, setSelectedData] = useState(defaultSelectedDataState);
  const [isShowMorePressed, setIsShowMorePressed] = useState(false);
  const [numberOfDataShown, setNumberOfDataShown] = useState(defaultNumberOfShownData);
  const dataLength = data?.length || 0;
  const theme = useTheme();

  const isShowMoreVisible =
    withShowMore && dataLength > defaultNumberOfShownData && !isShowMorePressed;

  const isAllSelected = dataLength !== 0 && dataLength === selectedData.length;
  const sohoImages = getSohoThemeImages(theme.name);

  useEffect(() => {
    if (dataLength <= defaultNumberOfShownData) {
      setNumberOfDataShown(defaultNumberOfShownData);
    } else {
      isShowMorePressed && setNumberOfDataShown(dataLength);
    }
  }, [dataLength]);

  const onSelectUserPress = (isSelected, newDataObject) => {
    let newSelectedData = [],
      newUnselectedData = [];

    if (isSelected) {
      newSelectedData = [...selectedData, newDataObject];
    } else {
      newSelectedData = _filter(selectedData, _dataObject => _dataObject.id !== newDataObject.id);
    }

    newUnselectedData = data.filter(d => !newSelectedData.some(s => s.id === d.id));

    setSelectedData(newSelectedData);
    _isFunction(onSelectPress) && onSelectPress(newSelectedData, newUnselectedData);
  };

  const onShowMorePress = () => {
    setNumberOfDataShown(dataLength);
    setIsShowMorePressed(true);
  };

  const onShowLessPress = () => {
    setNumberOfDataShown(defaultNumberOfShownData);
    setIsShowMorePressed(false);
  };

  const onSelectAllPress = () => {
    isShowMoreVisible && onShowMorePress();
    setSelectedData(data);
    _isFunction(onSelectPress) && onSelectPress(data, []);
  };

  const renderTitle = () => {
    return (
      <View style={styles.titleContainerStyle}>
        <VFText
          i18nKey={title}
          style={styles.titleTextStyle}
          type="primary"
          testKey={titleTestKey}
        />
        {withSelect && dataLength !== 0 && (
          <TouchableOpacity
            testID={SohoFrameworkTestKeys.SelectionListSelectAllBtn}
            onPress={onSelectAllPress}
            disabled={isAllSelected}
          >
            <VFText
              i18nKey={selectAllButtonTitle}
              style={[
                styles.selectAllTextStyle,
                isAllSelected ? styles.selectAllDimmedTextStyle : {},
              ]}
              type="textWithIcon"
            />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderShowMoreButton = () => {
    if (isShowMoreVisible) {
      return (
        <TouchableOpacity
          testID={SohoFrameworkTestKeys.SelectionListShowMoreBtn}
          onPress={onShowMorePress}
        >
          <View style={[styles.showBtnContainerStyle, styles.showMoreContainer]}>
            <VFText i18nKey={showMoreButtonTitle} type="tertiary" style={styles.showBtnStyle} />
          </View>
        </TouchableOpacity>
      );
    }
    return <View style={styles.marginStyle} />;
  };

  const renderShowLessButton = () => {
    if (isShowMorePressed) {
      return (
        <TouchableOpacity
          testID={SohoFrameworkTestKeys.SelectionListShowLessBtn}
          onPress={onShowLessPress}
        >
          <View style={styles.showBtnContainerStyle}>
            <VFText i18nKey={showLessButtonTitle} type="tertiary" style={styles.showBtnStyle} />
          </View>
        </TouchableOpacity>
      );
    }
  };

  const getIcon = icon => {
    if (icon) {
      return images?.[icon] || icon;
    }
    return sohoImages.ic_business;
  };

  const renderData = () => {
    if (dataLength === 0) {
      return (
        <View style={styles.emptyViewStyle(theme)}>
          <Icon name={sohoImages.icInfoCircle} type="image" size={25} />
          <VFText i18nKey={emptyText} style={styles.emptyTextStyle} type="primary" />
        </View>
      );
    }
    return data?.slice(0, numberOfDataShown).map(obj => {
      const id = obj.id;
      return (
        <SelectionItem
          key={id}
          title={obj[dataTitleKey]}
          subTitle={obj[dataSubTitleKey]}
          subTitlePlaceholders={obj[dataSubTitlePlaceholdersKey]}
          icon={getIcon(obj?.icon)}
          withSelect
          onSelectPress={isSelected => onSelectUserPress(isSelected, obj)}
          isSelected={_findIndex(selectedData, _obj => _obj.id === id) !== -1}
          selectTestKey={selectTestKey}
          titleTestKey={selectionTitleTestKey}
          removeTestKey={SohoFrameworkTestKeys.SelectionListRemoveIcon}
        />
      );
    });
  };
  const renderButtons = () => {
    if (_isFunction(renderButtonsSection)) {
      return renderButtonsSection();
    }
    return (
      <View style={styles.btnContainerStyle}>
        {!!primaryButtonTitle && (
          <VFButton
            title={primaryButtonTitle}
            type="primary"
            disabled={_get(selectedData, 'length', 0) === 0}
            style={styles.primaryButtonStyle}
            onPress={() => onPrimaryButtonPress(selectedData)}
            testKey={primaryButtonTestKey}
          />
        )}
        {!!secondaryButtonTitle && (
          <VFButton
            title={secondaryButtonTitle}
            type="secondary"
            disabled={_get(selectedData, 'length', 0) === 0}
            style={styles.secondaryButtonStyle}
            onPress={() => onSecondaryButtonPress(selectedData)}
            testKey={secondaryButtonTestKey}
          />
        )}
      </View>
    );
  };

  return (
    <View {...props}>
      {renderTitle()}
      {renderData()}
      {renderShowMoreButton()}
      {renderShowLessButton()}
      {renderButtons()}
    </View>
  );
}
SelectionList.defaultProps = {
  defaultSelectedDataState: [],
};
SelectionList.propTypes = {
  title: PropTypes.string,
  emptyText: PropTypes.string,
  onPrimaryButtonPress: PropTypes.func,
  onSecondaryButtonPress: PropTypes.func,
  onSelectPress: PropTypes.func,
  images: PropTypes.object,
  renderButtonsSection: PropTypes.func,
  selectAllButtonTitle: PropTypes.string,
  showMoreButtonTitle: PropTypes.string,
  showLessButtonTitle: PropTypes.string,
  primaryButtonTitle: PropTypes.string,
  secondaryButtonTitle: PropTypes.string,
  withSelect: PropTypes.bool,
  withShowMore: PropTypes.bool,
  dataConfig: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
    dataTitleKey: PropTypes.string,
    dataSubTitleKey: PropTypes.string,
    dataSubTitlePlaceholdersKey: PropTypes.string,
    defaultNumberOfShownData: PropTypes.number.isRequired,
  }),
  testKeys: PropTypes.shape({
    selectionTitleTestKey: PropTypes.string,
    selectTestKey: PropTypes.string,
    titleTestKey: PropTypes.string,
    primaryButtonTestKey: PropTypes.string,
    secondaryButtonTestKey: PropTypes.string,
  }),
  defaultSelectedDataState: PropTypes.array,
};
export default SelectionList;
